import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Box, Paper, TextField, Typography } from "@mui/material";
import { memo } from "react";
import PropTypes from "prop-types";

import { companyNationalIdInputProps } from "../../../../../../infrastructure/utils/companyNationalIdRules";
import { wrapFormikHandleChange } from "../../../../../../infrastructure/utils/formatters";
import DropdownPicker from "../../../../../components/shared/dropdown";
import JalaliDatePicker from "../../../../../components/shared/jalali-datepicker";
import { getApplicantIdentityFields } from "../checkNewRequestPageUtils";
import {
  CRM_CARD_BODY_SX,
  CRM_CARD_HEADER_SX,
  CRM_CARD_SX,
  CRM_FIELD_GRID,
  CRM_SECTION_TITLE_SX,
} from "./initialReviewStyles";
import {
  FORM_FIELD_SIZE,
  applicantGenderOptions,
} from "./initialReviewConstants";

const displayValue = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

const CustomerInfoCard = memo(
  ({
    values,
    fieldDisabled = false,
    onMakeFormikVariable,
    handleChange,
    setFieldValue,
    errors = {},
    touched = {},
  }) => {
    const identity = getApplicantIdentityFields(values);

    return (
      <Paper elevation={0} sx={CRM_CARD_SX}>
        <Box sx={CRM_CARD_HEADER_SX}>
          <StackTitle icon={PersonOutlineIcon} title="اطلاعات تماس متقاضی" />
        </Box>
        <Box sx={CRM_CARD_BODY_SX}>
          <Box sx={CRM_FIELD_GRID}>
            <TextField
              label="نام"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("firstName")}
            />
            <TextField
              label="نام خانوادگی"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("lastName")}
            />
            <TextField
              label="کد ملی"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              {...onMakeFormikVariable("nationalCode")}
            />
            <TextField
              label="نام پدر"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("fatherName")}
            />
            <JalaliDatePicker
              label="تاریخ تولد"
              size={FORM_FIELD_SIZE}
              value={values.birthDate}
              onChange={(date) => setFieldValue?.("birthDate", date)}
              error={Boolean(errors.birthDate && touched.birthDate)}
              helperText={
                errors.birthDate && touched.birthDate ? errors.birthDate : ""
              }
              disabled={fieldDisabled}
              fullWidth
            />
            <DropdownPicker
              size={FORM_FIELD_SIZE}
              label="جنسیت"
              list={applicantGenderOptions}
              optionName="name"
              optionValue="value"
              filterable={false}
              value={
                values.gender != null && values.gender !== ""
                  ? String(values.gender)
                  : ""
              }
              onChange={(event) => setFieldValue?.("gender", event.target.value)}
              disabled={fieldDisabled}
              fullWidth
            />
            <TextField
              label="موبایل"
              size={FORM_FIELD_SIZE}
              disabled
              fullWidth
              variant="outlined"
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-root": { bgcolor: "grey.50" } }}
              {...onMakeFormikVariable("mobile")}
            />
            <TextField
              label="نوع شخص"
              size={FORM_FIELD_SIZE}
              value={displayValue(
                identity.agreementKindTitle || values.agreementKindTitle
              )}
              disabled
              fullWidth
              variant="outlined"
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-root": { bgcolor: "grey.50" } }}
            />
            <TextField
              label="تلفن ثابت"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("telephon")}
            />
            <TextField
              label="کد پستی"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("postalCode")}
            />
            <TextField
              label="ایمیل"
              size={FORM_FIELD_SIZE}
              disabled={fieldDisabled}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("email")}
            />
          </Box>

          {values.agreementKind === "Legal" ? (
            <Box sx={{ ...CRM_FIELD_GRID, mt: 1.5 }}>
              <TextField
                label="شناسه ملی شرکت"
                size={FORM_FIELD_SIZE}
                disabled={fieldDisabled}
                onChange={wrapFormikHandleChange(handleChange)}
                inputProps={companyNationalIdInputProps}
                {...onMakeFormikVariable("companyNationalCode")}
              />
              <TextField
                label="شماره ثبت شرکت"
                size={FORM_FIELD_SIZE}
                disabled={fieldDisabled}
                onChange={wrapFormikHandleChange(handleChange)}
                {...onMakeFormikVariable("companyRegisterationCode")}
              />
            </Box>
          ) : null}
        </Box>
      </Paper>
    );
  }
);

const StackTitle = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Icon sx={{ fontSize: 18, color: "primary.main" }} />
    <Typography sx={CRM_SECTION_TITLE_SX}>{title}</Typography>
  </Box>
);

StackTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

CustomerInfoCard.displayName = "CustomerInfoCard";

CustomerInfoCard.propTypes = {
  values: PropTypes.object.isRequired,
  fieldDisabled: PropTypes.bool,
  onMakeFormikVariable: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func,
  errors: PropTypes.object,
  touched: PropTypes.object,
};

export default CustomerInfoCard;
